import {
  ApiError,
  apiGet,
  apiPost,
  el,
  formatDate,
  getConfig,
  gravatarUrl,
  initialsAvatar,
  parseUtc,
  renderChallenge,
  waitForToken,
  type Challenge,
  type SeopyoConfig,
} from "./shared.js";
import { errorText, resolveStrings, type Catalogue } from "./i18n.js";

// Mounts into every <div data-comments data-slug="..."> on the page.

interface Comment {
  id: number;
  parent_id: number | null;
  author_name: string;
  author_email_hash: string;
  body: string;
  /** The owner's heart, 0 or 1, as the column stores it. */
  hearted: number;
  created_at: string;
}

/** `comment` is present only when the comment went live. */
interface PostResult {
  id: number;
  status: "pending" | "approved";
  comment?: Comment;
}

// GET /comments is cached for 30 seconds, and the reader who just posted is
// the only one who can tell a stale list is stale. So rather than weaken the
// cache for everyone, remember that *this* browser posted and bypass it for
// them alone until their comment comes back in the list. Every storage access
// is guarded: some privacy settings make it throw outright.

const POSTED_PREFIX = "seopyo.posted.";

/** A hard expiry, for the case where the comment never does come back — a
 *  moderator took it down — so a bypass can't outlive its reason. */
const POSTED_TTL_MS = 60 * 60 * 1000;

interface Posted {
  id: number;
  at: number;
}

function rememberPosted(slug: string, id: number): void {
  try {
    const posted: Posted = { id, at: Date.now() };
    sessionStorage.setItem(POSTED_PREFIX + slug, JSON.stringify(posted));
  } catch {
    /* no storage; the appended comment still shows for this page load */
  }
}

function forgetPosted(slug: string): void {
  try {
    sessionStorage.removeItem(POSTED_PREFIX + slug);
  } catch {
    /* nothing to forget */
  }
}

/** The live marker for this slug, or null. Expired markers are dropped. */
function readPosted(slug: string): Posted | null {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(POSTED_PREFIX + slug);
  } catch {
    return null;
  }
  if (!stored) return null;

  let posted: Posted;
  try {
    posted = JSON.parse(stored) as Posted;
  } catch {
    forgetPosted(slug);
    return null;
  }

  // Anything unreadable is treated as absent rather than trusted: this is
  // storage another script on the blog's page could have written.
  if (typeof posted?.id !== "number" || typeof posted?.at !== "number") {
    forgetPosted(slug);
    return null;
  }

  if (Date.now() - posted.at > POSTED_TTL_MS) {
    forgetPosted(slug);
    return null;
  }
  return posted;
}

/** How deep replies are drawn. Deeper ones are stored as they were written
 *  and drawn at this depth, beside the comment they answer. */
const MAX_DEPTH = 3;

interface Placed {
  comment: Comment;
  depth: number;
  item: HTMLLIElement;
  container: HTMLUListElement;
  replies: HTMLUListElement | null;
}

class CommentsWidget {
  private readonly list = el("ul", { class: "seopyo-list" });
  private readonly status = el("p", { class: "seopyo-status" });
  private readonly formSlot = el("div");
  private readonly placed = new Map<number, Placed>();
  private replyTarget: Comment | null = null;
  private readonly replyingText = el("span");
  private readonly replying = el(
    "div",
    { class: "seopyo-replying", hidden: true },
    this.replyingText,
  );

  constructor(
    private readonly root: HTMLElement,
    private readonly slug: string,
    private readonly config: SeopyoConfig,
    private readonly text: Catalogue,
    private readonly owner: string,
  ) {}

  async init(): Promise<void> {
    this.root.classList.add("seopyo");
    // The div belongs to the blog, whose own lang may differ from the widget's.
    this.root.lang = this.text.lang;
    this.status.textContent = this.text.ui.loading;
    // No heading: the page around this has its own outline, and a wrong-level
    // <h2> in the middle of it is worse than none.
    this.root.replaceChildren(this.status, this.list, this.formSlot);
    await this.buildForm();
    await this.load();
  }

  private async load(): Promise<void> {
    const posted = readPosted(this.slug);
    // A distinct URL, rather than fetch's cache:"reload": a request cache mode
    // only defeats the browser's own cache, and Cloudflare's edge does not let
    // a client bust it with a request header.
    const bust = posted ? `&_=${Date.now()}` : "";

    try {
      const { comments } = await apiGet<{ comments: Comment[] }>(
        `/comments?slug=${encodeURIComponent(this.slug)}${bust}`,
      );
      // The cache is no longer hiding their comment, so stop busting it.
      if (posted && comments.some((comment) => comment.id === posted.id))
        forgetPosted(this.slug);
      this.renderList(comments);
    } catch {
      this.status.className = "seopyo-error";
      this.status.textContent = this.text.ui.loadFailed;
    }
  }

  private renderList(comments: Comment[]): void {
    this.status.className = "seopyo-status";
    this.status.textContent = comments.length === 0 ? this.text.ui.empty : "";
    this.list.replaceChildren();
    this.placed.clear();
    for (const comment of comments) this.place(comment);
  }

  private appendComment(comment: Comment): void {
    this.status.textContent = "";
    this.place(comment);
  }

  /** Comments arrive oldest first, so a parent is always placed before its
   *  replies. */
  private place(comment: Comment): void {
    const parent =
      comment.parent_id === null
        ? undefined
        : this.placed.get(comment.parent_id);

    let container = this.list;
    let depth = 1;
    let answering: string | undefined;
    if (parent && parent.depth < MAX_DEPTH) {
      if (!parent.replies) {
        parent.replies = el("ul", { class: "seopyo-replies" });
        parent.item.appendChild(parent.replies);
      }
      container = parent.replies;
      depth = parent.depth + 1;
    } else if (parent) {
      // Drawn beside its parent rather than under it, so it says who it answers.
      container = parent.container;
      depth = MAX_DEPTH;
      answering = parent.comment.author_name;
    }

    const item = this.renderComment(comment, answering);
    container.appendChild(item);
    this.placed.set(comment.id, {
      comment,
      depth,
      item,
      container,
      replies: null,
    });
  }

  private startReply(comment: Comment): void {
    this.replyTarget = comment;
    this.replyingText.textContent = this.text.ui.replyTo(comment.author_name);
    this.replying.hidden = false;
    this.formSlot.querySelector("textarea")?.focus();
  }

  private cancelReply(): void {
    this.replyTarget = null;
    this.replying.hidden = true;
  }

  private renderHeart(comment: Comment): HTMLElement | null {
    if (!comment.hearted) return null;
    return el(
      "span",
      { class: "seopyo-heart" },
      el("span", { class: "seopyo-heart-mark", "aria-hidden": "true" }, "♥"),
      el(
        "span",
        { class: "seopyo-heart-label" },
        this.text.ui.hearted(this.owner),
      ),
    );
  }

  private renderComment(comment: Comment, answering?: string): HTMLLIElement {
    const date = parseUtc(comment.created_at);
    const reply = el(
      "button",
      { type: "button", class: "seopyo-reply" },
      this.text.ui.reply,
    );
    reply.addEventListener("click", () => this.startReply(comment));

    return el(
      "li",
      { class: "seopyo-comment", "data-comment-id": comment.id },
      el(
        "div",
        { class: "seopyo-head" },
        this.config.gravatar
          ? el("img", {
              class: "seopyo-avatar",
              src: gravatarUrl(comment.author_email_hash),
              alt: "",
              width: 32,
              height: 32,
              loading: "lazy",
              referrerpolicy: "no-referrer",
            })
          : initialsAvatar(comment.author_name, comment.author_email_hash),
        el("span", { class: "seopyo-author" }, comment.author_name),
        el(
          "time",
          {
            class: "seopyo-date",
            datetime: Number.isNaN(date.getTime())
              ? undefined
              : date.toISOString(),
          },
          formatDate(comment.created_at, this.text.lang),
        ),
        reply,
      ),
      answering === undefined
        ? null
        : el(
            "div",
            { class: "seopyo-answering" },
            this.text.ui.replyTo(answering),
          ),
      el("div", { class: "seopyo-body" }, comment.body),
      this.renderHeart(comment),
    );
  }

  private async buildForm(): Promise<void> {
    const challengeSlot = el("div");
    const submit = el(
      "button",
      { type: "submit", class: "seopyo-submit" },
      this.text.ui.submit,
    );
    const message = el("span", { class: "seopyo-status" });
    const cancel = el(
      "button",
      { type: "button", class: "seopyo-cancel" },
      this.text.ui.cancelReply,
    );
    cancel.addEventListener("click", () => this.cancelReply());
    this.replying.appendChild(cancel);

    const form = el(
      "form",
      { class: "seopyo-form", novalidate: false },
      this.replying,
      el(
        "div",
        { class: "seopyo-fields" },
        el(
          "div",
          { class: "seopyo-field" },
          el("label", { for: "seopyo-name" }, this.text.ui.nameLabel),
          el("input", {
            id: "seopyo-name",
            name: "name",
            type: "text",
            maxlength: 64,
            required: true,
            autocomplete: "name",
          }),
        ),
        el(
          "div",
          { class: "seopyo-field" },
          el("label", { for: "seopyo-email" }, this.text.ui.emailLabel),
          // type="email" is for the mobile keyboard and autofill; the server
          // deliberately does not check the format.
          el("input", {
            id: "seopyo-email",
            name: "email",
            type: "email",
            maxlength: 254,
            required: true,
            autocomplete: "email",
          }),
        ),
      ),
      el(
        "div",
        { class: "seopyo-field" },
        el("label", { for: "seopyo-body" }, this.text.ui.bodyLabel),
        el("textarea", {
          id: "seopyo-body",
          name: "body",
          maxlength: 4000,
          required: true,
        }),
      ),
      challengeSlot,
      el("div", { class: "seopyo-actions" }, submit, message),
    );

    // In the document before the challenge renders: Turnstile injects an
    // iframe, and an iframe in a detached subtree never starts loading.
    this.formSlot.appendChild(form);

    let challenge: Challenge | null = null;
    if (this.config.turnstile_sitekey) {
      try {
        challenge = await renderChallenge(
          challengeSlot,
          this.config.turnstile_sitekey,
          this.text.lang,
        );
      } catch {
        // Leave `challenge` null: the submit below reports the failure rather
        // than posting something the server will reject.
      }
    }

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;

      submit.setAttribute("disabled", "");
      message.className = "seopyo-status";
      message.textContent = this.text.ui.posting;

      try {
        const token = challenge ? await waitForToken(challenge) : "";
        // A code deliberately absent from the catalogue: the message is already
        // in the reader's language, so errorText passes it through.
        if (!token)
          throw new ApiError(this.text.ui.spamTimeout, "spam_timeout");

        const result = await apiPost<PostResult>("/comments", {
          slug: this.slug,
          parent_id: this.replyTarget?.id ?? null,
          author_name: (form.elements.namedItem("name") as HTMLInputElement)
            .value,
          author_email: (form.elements.namedItem("email") as HTMLInputElement)
            .value,
          body: (form.elements.namedItem("body") as HTMLTextAreaElement).value,
          turnstile_token: token,
        });

        form.reset();
        challenge?.reset();
        this.cancelReply();

        if (result.status === "approved" && result.comment) {
          this.appendComment(result.comment);
          // Only for a live comment: a held one is not in the public list, so
          // bypassing the cache would find nothing until it is approved.
          rememberPosted(this.slug, result.comment.id);
          message.textContent = this.text.ui.posted;
        } else {
          message.textContent = this.text.ui.pending;
        }
      } catch (error) {
        message.className = "seopyo-error";
        message.textContent =
          error instanceof ApiError
            ? errorText(this.text, error.code, error.message)
            : this.text.ui.postFailed;
        challenge?.reset();
      } finally {
        submit.removeAttribute("disabled");
      }
    });
  }
}

async function mountAll(): Promise<void> {
  const targets = document.querySelectorAll<HTMLElement>("[data-comments]");
  if (targets.length === 0) return;

  // No stylesheet is injected: seopyo.css is a file the page links, because a
  // <style> element built here would oblige every blog with a CSP to allow
  // 'unsafe-inline' in style-src. See docs/INTEGRATION.md.
  const config = await getConfig();

  for (const target of targets) {
    const text = resolveStrings(
      target.dataset.lang,
      document.documentElement.lang,
    );

    // Required rather than defaulting to location.pathname, whose leading
    // slash and extension the slug grammar rejects — and a default would split
    // one page's comments in two the day the attribute was added.
    const slug = target.dataset.slug?.trim();
    if (!slug) {
      target.classList.add("seopyo");
      target.lang = text.lang;
      target.replaceChildren(
        el("p", { class: "seopyo-error" }, text.ui.missingSlug),
      );
      continue;
    }
    const owner = target.dataset.ownerName?.trim() || text.ui.owner;
    await new CommentsWidget(target, slug, config, text, owner).init();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => void mountAll());
} else {
  void mountAll();
}
